import { H4, Span } from '@/components/common/Typo'
import { productStatus } from '@/utils/product'

import QuantitySelector from '@/components/common/Forms/QuantitySelector'
import useAppContext from '@/hooks/useAppContext'
import { formatDiff } from '@/utils/number'
import { TFnType, useTranslate } from '@tolgee/react'
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  cn,
} from 'oddle-shop-monorepo.design-system'
import { ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  checkExistNutriGradeTag,
  NutriGradeIcon,
} from 'src/components/icons/NutriGrade'
import { getAllProductNutrigradeTags } from 'src/utils/product'
import {
  ContentContainer,
  Header,
  ItemContainer,
} from './ContentVariantElements'
import OptionThumbnail from './OptionThumbnail'

const getSubtitle = ({
  t,
  minimumQuantity,
  maximumQuantity,
}: {
  t: TFnType
  minimumQuantity: number
  maximumQuantity: number
}) => {
  if (minimumQuantity === maximumQuantity) {
    return t('Select {maximumQuantity}', { maximumQuantity })
  }
  if (minimumQuantity === 0) {
    return t('Select up to {maximumQuantity}', { maximumQuantity })
  } else {
    return t(
      'Select at least {minimumQuantity} up to a maximum of {maximumQuantity}',
      { minimumQuantity, maximumQuantity }
    )
  }
}

const SelectedQuantity = ({
  children,
  satisfied,
}: {
  children: ReactNode
  satisfied: boolean
}) => {
  return (
    <Span className={satisfied ? 'text-[#393939]' : 'text-red-500'}>
      {children}
    </Span>
  )
}

export const OptionHeader = ({
  option,
  show = true,
  hideArrow = false,
  touched = false,
  formatMoney = (x) => x.toString(),
}: {
  option: Oms.Subitems
  show?: boolean
  hideArrow?: boolean
  touched?: boolean
  formatMoney?: (x: number) => string
}) => {
  const { t } = useTranslate()
  const {
    description,
    minimumQuantity = 0,
    maximumQuantity = 0,
    selectedQuantity = 0,
    totalSubPrice = 0,
  } = option || {}
  const isMinQuantitySatisfied = minimumQuantity <= selectedQuantity
  const subtitle = getSubtitle({ t, minimumQuantity, maximumQuantity })

  return (
    <Header
      show={show}
      hideArrow={hideArrow}
      title={description}
      subtitle={subtitle}
      body={
        <SelectedQuantity satisfied={isMinQuantitySatisfied}>
          {t('{quantity} selected', { quantity: selectedQuantity || '0' })}
        </SelectedQuantity>
      }
      bodySubText={
        totalSubPrice === 0
          ? undefined
          : formatDiff({ value: totalSubPrice, formatter: formatMoney })
      }
      error={touched && !isMinQuantitySatisfied}
    />
  )
}

interface ProductOptionProps {
  hideArrow: boolean
  option: Oms.Subitems
  calculateOptionDiff: (optionId: string, variantId: string) => number
  open: boolean
}

const getOptionIdentifier = ({
  option,
  variant,
}: {
  option: {
    id: string
    maximumQuantity: number
    minimumQuantity: number
    selectedQuantity: number
  }
  variant: {
    id: string
  }
}) => `subitems.${option.id}.${variant.id}`

const OptionContent = ({
  show = true,
  withInput = false,
  option,
  renderImage,
  calculateOptionDiff,
}: {
  show: boolean
  withInput: boolean
  option: Oms.Subitems
  renderImage: (x: { variant: any }) => React.ReactNode
  calculateOptionDiff: (optionId: string, variantId: string) => number
}) => {
  const { t } = useTranslate()
  const { formatMoney } = useAppContext()
  const { maximumQuantity, selectedQuantity = 0 } = option
  const remainingQuantity = Number(maximumQuantity) - Number(selectedQuantity)
  const disableIncrease = remainingQuantity === 0
  const { getValues, setValue } = useFormContext()

  const onQuantityChange = (name: string, value: number) => {
    const subitems = getValues('subitems') || {}
    const splitName = name.split('.')
    const optionId = splitName[1] as string
    const variantId = splitName[2] as string

    // Must always replace the whole subitems object (avoid mutating frozen obj)
    if (!subitems[optionId]) {
      // Create a new object to ensure mutability
      setValue('subitems', {
        ...subitems,
        [optionId]: {
          [variantId]: value,
        },
      })
    } else {
      // Also do not mutate existing frozen objects
      setValue('subitems', {
        ...subitems,
        [optionId]: {
          ...subitems[optionId],
          [variantId]: value,
        },
      })
    }
  }

  return (
    <ContentContainer show={show}>
      {option.variants?.map((variant: any, key: number) => {
        const diff = calculateOptionDiff(option.id, variant.id)
        const disabled =
          variant.status !== undefined &&
          variant.status !== productStatus.AVAILABLE
        const existNutriGradeTag = checkExistNutriGradeTag(variant.tags)
        const productNutrigradeTags = getAllProductNutrigradeTags(variant.tags)
        const last = key === option.variants.length - 1

        return (
          <ItemContainer key={variant.id} disabled={disabled} last={last}>
            {renderImage({ variant })}
            {(existNutriGradeTag || productNutrigradeTags) && (
              <div className="flex">
                <NutriGradeIcon tags={variant.tags} height={40} width={89} />
                {productNutrigradeTags && (
                  <div className="ml-1">
                    <div className="mb-[10px] flex flex-wrap gap-[5px] p-0">
                      {productNutrigradeTags.map((tag: string) => (
                        <div
                          key={tag}
                          className="font-roboto inline-block rounded-[var(--button-radius)] border border-[#bbb] bg-white px-[5px] py-[4px] text-[var(--small-text-size)] text-black"
                        >
                          {tag}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex grow items-start justify-between">
              <div
                className={`relative flex flex-col mt-${
                  existNutriGradeTag || productNutrigradeTags
                    ? '[var(--margin-small)]'
                    : '0'
                } self-center`}
              >
                <H4
                  className="text-base font-medium"
                  marginTop="none"
                  marginBottom={
                    existNutriGradeTag || productNutrigradeTags
                      ? 'extraSmall'
                      : 'small'
                  }
                >
                  {variant.variantName}
                </H4>
                <H4
                  className="text-base"
                  marginTop="extraSmall"
                  marginBottom="none"
                  data-testid={`text.priceDiff.${key}`}
                  bold
                >
                  {disabled
                    ? t('Not available')
                    : diff > 0 &&
                      !disableIncrease &&
                      formatDiff({
                        value: diff,
                        formatter: formatMoney,
                      })}
                </H4>
              </div>
              <div
                className={cn(
                  'flex items-center justify-between self-stretch',
                  existNutriGradeTag || productNutrigradeTags
                    ? 'mt-2.5'
                    : 'mt-0'
                )}
              >
                <QuantitySelector
                  withInput={withInput}
                  onChange={onQuantityChange}
                  disabled={disabled}
                  name={getOptionIdentifier({ option, variant })}
                  disableIncrease={disableIncrease}
                  max={option.maximumQuantity}
                />
              </div>
            </div>
          </ItemContainer>
        )
      })}
    </ContentContainer>
  )
}

const ProductOption = ({
  hideArrow = false,
  option,
  calculateOptionDiff,
  open,
}: ProductOptionProps): ReactNode => {
  const { formatMoney } = useAppContext()
  const { formState } = useFormContext()

  return (
    <AccordionItem
      id={option.id}
      value={option.id}
      className="border-b-0 outline-none"
    >
      <AccordionTrigger className="outline-none hover:no-underline">
        <OptionHeader
          show={open}
          option={option}
          hideArrow={hideArrow}
          touched={formState.isSubmitted || formState.isSubmitting}
          formatMoney={formatMoney}
        />
      </AccordionTrigger>
      <AccordionContent>
        <OptionContent
          withInput
          option={option}
          show={open}
          calculateOptionDiff={calculateOptionDiff}
          renderImage={({ variant }: { variant: any }) => (
            <OptionThumbnail variant={variant} />
          )}
        />
      </AccordionContent>
    </AccordionItem>
  )
}

export default ProductOption
